import { getTrackingId } from '../utils/helper';
import { IdToken, User } from './types';

export type MaybePromise<T> = Promise<T> | T;

export interface DecodedToken {
  claims: IdToken;
  user: User;
}

export type CacheEntry = {
  idToken?: string;
  accessToken: string;
  expiresIn: number;
  decodedToken?: DecodedToken;
  audience: string;
  scope: string;
  refreshToken?: string;
};

export type CacheableAuth = {
  body: Partial<CacheEntry>;
  expiresAt: number;
};

export type AppMetadata = {
  verificationCompleted?: boolean;
  otp: string;
};

interface ICache {
  set<T = CacheableAuth>(key: string, entry: T): void;
  get<T = CacheableAuth>(key: string): T | undefined;
  removeAll(): void;
}

const applicant = new URLSearchParams(window.location.search)?.get('joint') ? 'joint' : 'primary';
export const AUTH_CACHE_KEY_PREFIX = '@@auth0-';
export const getFormattedAuthCacheKey = (key?: string) => `${AUTH_CACHE_KEY_PREFIX}${key}-${applicant}-@@`;

export const APP_METADATA_CACHE_KEY_PREFIX = '@@app-metadata-';
export const getFormattedAppMetadataCacheKey = (key?: string) =>
  `${APP_METADATA_CACHE_KEY_PREFIX}${key}-${applicant}-@@`;

export class LocalStorageCache implements ICache {
  public set<T = CacheableAuth>(key: string, entry: T) {
    window.localStorage.setItem(key, JSON.stringify(entry));
  }

  public get<T = CacheableAuth>(key: string): T | undefined {
    const json = window.localStorage.getItem(key);

    if (!json) return;

    try {
      const payload = JSON.parse(json) as T;
      return payload;
    } catch (e) {
      return;
    }
  }

  public removeAll(key?: string) {
    [getFormattedAuthCacheKey(key), getFormattedAppMetadataCacheKey(key)].forEach(prefix => {
      const keysToRemove = Object.keys(window.localStorage).filter(key => key.startsWith(prefix));
      keysToRemove.forEach(key => window.localStorage.removeItem(key));
    });
  }
}

const RESEND_CODE_CACHE_KEY_PREFIX = getFormattedAuthCacheKey('resend-count');
const DEFAULT_RESEND_WAIT_SECONDS = 30;
const EXTENDED_RESEND_WAIT_SECONDS = 60;
const EXTENDED_RESEND_THRESHOLD = 2;

type ResendCodeEntry = {
  count: number;
  updatedAt: number;
};

const getResendCodeStorageKey = () => {
  const trackingId = getTrackingId().trim();
  return `${RESEND_CODE_CACHE_KEY_PREFIX}${trackingId || 'default'}`;
};

const readResendEntry = (): ResendCodeEntry | null => {
  const cache = new LocalStorageCache();
  return cache.get<ResendCodeEntry>(getResendCodeStorageKey()) ?? null;
};

export const getResendCodeCount = () => readResendEntry()?.count ?? 0;

export const getResendWaitSecondsForCount = (count: number) =>
  count > EXTENDED_RESEND_THRESHOLD ? EXTENDED_RESEND_WAIT_SECONDS : DEFAULT_RESEND_WAIT_SECONDS;

export const getResendWaitSeconds = () => getResendWaitSecondsForCount(getResendCodeCount());

export const incrementResendCodeCount = () => {
  const cache = new LocalStorageCache();
  const currentCount = getResendCodeCount();
  const nextCount = currentCount + 1;
  const entry: ResendCodeEntry = {
    count: nextCount,
    updatedAt: Date.now(),
  };

  cache.set(getResendCodeStorageKey(), entry);
  return nextCount;
};

export const clearResendCodeCount = () => {
  window.localStorage.removeItem(getResendCodeStorageKey());
};

export const clearAllResendCodeCounts = () => {
  const keysToRemove = Object.keys(window.localStorage).filter(key => key.startsWith(RESEND_CODE_CACHE_KEY_PREFIX));
  keysToRemove.forEach(key => window.localStorage.removeItem(key));
};
